import { create } from 'zustand';
import type { Lobby, LobbyMember, LobbyTalkId, Ruleset, Ticket } from '@cribbage/protocol';
import { ApiError, type ApiClient, api } from '~/api/client.js';
import { generation } from '~/session/generation.js';
import { type Flight, flight } from '~/session/singleFlight.js';

/**
 * "Which online thing am I in right now" — the queued ticket, the private
 * lobby, and the game either of them hands us — plus the polling that keeps
 * them fresh.
 *
 * Short-polling rather than a socket is deliberate (see docs/dev/build_plan_2026_08.md): the ticket and
 * lobby endpoints are cheap, and a 2s poll rides out the flaky mobile networks
 * a persistent connection would spend its life re-establishing. The websocket
 * only comes out once there is a room to join.
 */

export type GameMode = Ticket['mode'];
export type GameFormat = Ticket['format'];
export type BotDifficulty = NonNullable<LobbyMember['bot_difficulty']>;

export const POLL_INTERVAL_MS = 2_000;

/** Returns its own canceller, so the poller never touches a timer id. */
export type Schedule = (delayMs: number, fn: () => void) => () => void;

const timerSchedule: Schedule = (delayMs, fn) => {
  const handle = setTimeout(fn, delayMs);
  return () => {
    clearTimeout(handle);
  };
};

/* --------------------------------------------------------------- polling */

export type PollDecision = 'continue' | 'stop';

export interface Poller {
  stop(): void;
}

export interface PollOptions<T> {
  readonly run: () => Promise<T>;
  readonly onResult: (value: T) => PollDecision;
  /** Omitted means any failure ends the poll. */
  readonly onError?: (error: unknown) => PollDecision;
  readonly intervalMs?: number;
  readonly schedule?: Schedule;
  /** Ask immediately instead of waiting out the first interval. */
  readonly immediate?: boolean;
}

/**
 * A chain of timeouts, not `setInterval`: on a slow link a request can outlive
 * the interval, and piling further requests onto a connection that is already
 * struggling turns a poll into a stampede.
 */
export function poll<T>(options: PollOptions<T>): Poller {
  const intervalMs = options.intervalMs ?? POLL_INTERVAL_MS;
  const schedule = options.schedule ?? timerSchedule;
  let cancelTimer: (() => void) | null = null;
  let stopped = false;

  function again(): void {
    if (stopped) return;
    cancelTimer = schedule(intervalMs, tick);
  }

  function tick(): void {
    if (stopped) return;
    void options.run().then(
      (value) => {
        if (!stopped && options.onResult(value) === 'continue') again();
      },
      (error: unknown) => {
        if (!stopped && (options.onError?.(error) ?? 'stop') === 'continue') again();
      },
    );
  }

  if (options.immediate === true) tick();
  else again();

  return {
    stop(): void {
      stopped = true;
      cancelTimer?.();
      cancelTimer = null;
    },
  };
}

/**
 * A poll should ride out exactly the conditions it exists for — a dropped
 * connection or a server hiccup — and give up on the ones that will never fix
 * themselves, like a ticket that no longer exists.
 */
export function isTransient(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.status === 0 || error.status >= 500;
}

/* ---------------------------------------------------------------- errors */

export type ErrorKey =
  | 'errors.network'
  | 'errors.unauthorized'
  | 'errors.notFound'
  | 'errors.conflict'
  | 'errors.cancelFailed'
  | 'errors.invalid'
  | 'errors.rateLimited'
  | 'errors.server'
  | 'errors.unexpected';

export interface FriendlyError {
  /** An i18n key, so the store never holds a user-visible sentence. */
  readonly key: ErrorKey;
  /** Field-level messages authored by Rails; nothing to translate here. */
  readonly details: Record<string, readonly string[]> | null;
}

/**
 * `display_name` → `Display name`.
 *
 * Rails writes a validation message to sit after the humanized attribute name
 * ("can only be changed every 30 days"), so the field has to be printed for
 * the sentence to parse. The raw key is a snake_case identifier, and a player
 * should never be shown one.
 */
function humanizeField(field: string): string {
  const words = field.replaceAll('_', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Rails' refusal of one field as the sentence a player reads: "Display name
 * has already been taken." Every form that prints a server's per-field
 * `details` prints it this way, so the shape lives here once.
 */
export function refusalLine(field: string, messages: readonly string[]): string {
  return `${humanizeField(field)} ${messages.join(', ')}.`;
}

export function toFriendlyError(error: unknown): FriendlyError {
  if (!(error instanceof ApiError)) return { key: 'errors.unexpected', details: null };
  const details: Record<string, readonly string[]> | null = error.details ?? null;
  if (error.status === 0) return { key: 'errors.network', details };
  if (error.status === 401 || error.status === 403) return { key: 'errors.unauthorized', details };
  if (error.status === 404 || error.status === 410) return { key: 'errors.notFound', details };
  if (error.status === 409) return { key: 'errors.conflict', details };
  if (error.status === 422 || error.status === 400) return { key: 'errors.invalid', details };
  if (error.status === 429) return { key: 'errors.rateLimited', details };
  if (error.status >= 500) return { key: 'errors.server', details };
  return { key: 'errors.unexpected', details };
}

/* ----------------------------------------------------------------- store */

/**
 * How a Cancel ended. `left`: the server let the ticket go. `matched`: the
 * table won the race, and `gameId` now names it. `failed`: the server did not
 * answer, so the ticket is still live and the store is watching it again.
 */
export type LeaveOutcome = 'left' | 'matched' | 'failed';

export interface QueueRequest {
  readonly mode: GameMode;
  readonly format: GameFormat;
  readonly allowBotBackfill: boolean;
}

export interface SessionState {
  /** Injected in tests; production always talks to the shared client. */
  client: ApiClient;
  schedule: Schedule;

  ticket: Ticket | null;
  /** Local clock, so the elapsed wait cannot be skewed by the server's. */
  queuedAt: number | null;
  ticketError: FriendlyError | null;

  lobby: Lobby | null;
  lobbyError: FriendlyError | null;
  busy: boolean;

  /** Set the moment either path is handed a game to join. */
  gameId: string | null;

  enterQueue(request: QueueRequest): Promise<void>;
  /** `immediate` asks now rather than after the first interval. */
  watchTicket(id: string, immediate?: boolean): void;
  stopWatchingTicket(): void;
  /** Cancels the search, and keeps the ticket until the server lets it go. */
  leaveQueue(): Promise<LeaveOutcome>;
  /** Asks after the ticket again now: the retry after a lost connection. */
  recheckTicket(): void;
  /**
   * A table that formed as the player left the queue. The queue screen is
   * gone, so the app enters it from wherever they went (`StrayTableDoor`),
   * rather than leave the other players waiting at an empty seat.
   */
  strayGameId: string | null;
  /**
   * The session token the stray table was found for. The door enters it only
   * for that session: after a sign-out it is another player's table.
   */
  strayFor: string | null;
  /** Hands the table this search found to the app to enter, for `owner`'s session; a private table is untouched. */
  leaveForTable(gameId: string, owner: string): void;
  /** The queue screen is gone: a table found later is handed to the app to enter, for `owner`'s session. */
  queueScreenGone(owner: string): void;
  /** `StrayTableDoor` has taken the stray table; nothing more is owed. */
  strayTableTaken(): void;

  createLobby(
    mode: GameMode,
    format: GameFormat,
    ruleset?: Partial<Ruleset>,
  ): Promise<string | null>;
  joinLobby(code: string): Promise<string | null>;
  /** Gives the seat at this table up on the server, and forgets the table here. */
  leaveLobby(code: string): Promise<void>;
  watchLobby(code: string): void;
  stopWatchingLobby(): void;
  /** Seats a bot, or changes the difficulty of the bot already in the seat. */
  addBot(seat: number, difficulty: BotDifficulty): Promise<boolean>;
  removeBot(seat: number): Promise<boolean>;
  /** Resolves true once the game is under way, false when the start was refused. */
  startLobby(): Promise<boolean>;
  /**
   * Says one approved phrase at this table. Resolves whether Rails heard it.
   * Not a lobby write in `busy`'s sense: a phrase never disables the seats,
   * and a refused one is the tray's news, not the lobby's alert.
   */
  talkInLobby(id: LobbyTalkId): Promise<boolean>;

  reset(): void;
}

let ticketPoll: Poller | null = null;
let lobbyPoll: Poller | null = null;
/** Guards the non-idempotent POST against a double-invoked mount effect. */
let entering = false;
/** Searches and cancels: each one, and every reset, supersedes the last. */
const queueRequests = generation();
/* The ticket a Cancel is still waiting on. It stays in the store until the
   server lets it go, so a search started meanwhile must not resume it. */
let cancelling: string | null = null;
/* A Cancel that arrives while a create is still out waits on that create's
   outcome. The create, finding itself superseded, hands its ticket over
   instead of cancelling it blind: Rails may already have matched it into a
   table. Every Cancel pressed until that outcome settles joins it, the
   handover and the cancel in flight after it alike. */
interface Handed {
  /** The ticket the late create made, so a cancel that failed can keep watching it. */
  readonly ticket: Ticket | null;
  /**
   * Why the create made no ticket, if it failed. A lost reply is not proof
   * that nothing was made: Rails may have committed the ticket.
   */
  readonly error: unknown;
}
interface LateResult extends Handed {
  readonly value: LeaveOutcome | Ticket;
}
const lateWork = flight<LateResult>();
/** The waiting outcome's handover, until the superseded create uses it. */
let handLate: ((handed: Handed) => void) | null = null;
/* Creates still out, superseded or not. `entering` names only the current
   one, and a Cancel clears it; a second Cancel must still wait. */
let createsOut = 0;
/* Set when the queue screen goes away with its search unsettled. A table a
   late answer finds then goes to `strayGameId`, for `StrayTableDoor`, since no
   queue screen is left to enter it. The next search clears it. */
let queueOffScreen = false;
/** The session token the gone queue screen searched for; a late table is owed to it. */
let queueOwner: string | null = null;
/* Retired by every reset. A lobby request answers into the store only while
   the generation it started in is current: a reply that lands after the
   player has left would otherwise seat them at the table again. */
const lobbyRequests = generation();

/**
 * Rails answers a create with the player's existing queued ticket and discards
 * the mode and format just asked for. Ordering alone cannot prevent that: a
 * cancellation can be refused under contention, and the next search then reads
 * back the previous one's ticket. Adopting it would leave the player waiting
 * in — and matched into — a game they did not choose, so a ticket that does not
 * answer the request is treated as an unfinished cancellation.
 */
function answersRequest(ticket: Ticket, request: QueueRequest): boolean {
  return (
    ticket.mode === request.mode &&
    ticket.format === request.format &&
    // Consent, not a preference: a stale `true` here matches the player
    // against bots they turned off, in a game the matcher makes unranked.
    ticket.allow_bot_backfill === request.allowBotBackfill
  );
}

/** Rails reuses a queued ticket, so creates must wait for earlier cancellations. */
const queueWork = flight();
const sequenceQueue = queueWork.after;

/**
 * A cancel, asked once more when Rails answers 409: that means the ticket was
 * moving between games under the lock, and is still live.
 */
async function cancelConfirmed(client: ApiClient, id: string): Promise<void> {
  try {
    await client.cancelTicket(id);
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409) throw error;
    await client.cancelTicket(id);
  }
}

/**
 * Cancels a ticket, or says why not. A 404 is a refusal: the table won the
 * race, or the ticket is already history. The ticket is read back to tell
 * which, so a match is entered rather than abandoned.
 */
async function cancelOrRead(client: ApiClient, id: string): Promise<LeaveOutcome | Ticket> {
  try {
    await cancelConfirmed(client, id);
    return 'left';
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 404) return 'failed';
  }
  try {
    return await client.ticket(id);
  } catch (error) {
    return error instanceof ApiError && error.status === 404 ? 'left' : 'failed';
  }
}

/** How long a Cancel waits for a create that is still out before it gives up. */
export const CREATE_WAIT_MS = 5_000;

/** `work`'s value if it settles before CREATE_WAIT_MS passes; the timer is always cleared. */
async function settledWithin<T>(
  work: Promise<T>,
  schedule: Schedule,
): Promise<{ readonly value: T } | 'late'> {
  let stop = (): void => undefined;
  const late = new Promise<'late'>((resolve) => {
    stop = schedule(CREATE_WAIT_MS, () => {
      resolve('late');
    });
  });
  try {
    return await Promise.race([work.then((value) => ({ value })), late]);
  } finally {
    stop();
  }
}

/**
 * A superseded create's ticket. Handed to a Cancel waiting behind it, which
 * reads back whether it became a table; cancelled outright otherwise, as
 * after a reset, where no screen is left to enter a table.
 */
function abandon(client: ApiClient, ticket: Ticket): Promise<void> {
  if (handOver(ticket)) return Promise.resolve();
  return client.cancelTicket(ticket.id).then(
    () => undefined,
    () => undefined,
  );
}

/** Gives a superseded create's ticket, or null and its failure, to the Cancel waiting on it. */
function handOver(ticket: Ticket | null, error: unknown = null): boolean {
  if (handLate === null) return false;
  const hand = handLate;
  handLate = null;
  hand({ ticket, error });
  return true;
}

/**
 * A late create's failure that leaves its outcome unknown: the request may
 * have reached Rails, which may have queued the player. The search is not
 * said to have stopped; the player gets the connection retry instead.
 */
function lostReply(error: unknown): FriendlyError | null {
  return error !== null && isTransient(error) ? toFriendlyError(error) : null;
}

/**
 * What became of the create still out: cancelled, or the table Rails had
 * already found. Asked once, behind the queue, however many Cancels wait.
 */
function lateOutcome(client: ApiClient): Promise<LateResult> {
  return lateWork.join(() => {
    const created = new Promise<Handed>((resolve) => {
      handLate = resolve;
    });
    /* The place behind the queue is taken now, at the Cancel, so a search
       started afterwards posts only once this ticket is let go: Rails hands
       overlapping creates the same ticket. */
    const answer: { value: LeaveOutcome | Ticket; ticket: Ticket | null; error: unknown } = {
      value: 'left',
      ticket: null,
      error: null,
    };
    return sequenceQueue(async () => {
      ({ ticket: answer.ticket, error: answer.error } = await created);
      if (answer.ticket !== null) answer.value = await cancelOrRead(client, answer.ticket.id);
    }).then((): LateResult => answer);
  });
}

/**
 * Retires a search left over from another request before a new one starts.
 * Its failure is not the player's to see: `enterQueue` already treats a
 * ticket that outlived its cancellation as unfinished, and retires it again.
 */
function retire(ticket: Ticket): Promise<void> {
  queueRequests.retire();
  entering = false;
  ticketPoll?.stop();
  ticketPoll = null;
  const { client } = useSessionStore.getState();
  useSessionStore.setState({ ticket: null, queuedAt: null, ticketError: null, gameId: null });
  return sequenceQueue(async () => {
    await client.cancelTicket(ticket.id).catch(() => undefined);
  });
}

/* The leave still out for each table, by its code. A join to that table, or a
   read of its link, waits for it: Rails takes a seat back for a player who
   rejoins, and a late leave would then free the seat just taken. Another
   table's leave holds nothing up. One slot per table, kept once made: idle,
   it holds nothing. */
const leaving = new Map<string, Flight<void>>();

/** Settles once no leave for this table is still out. */
export function leaveSettled(code: string): Promise<void> {
  return leaving.get(code)?.idle() ?? Promise.resolve();
}

/* Tables seen closed since the last reset. A closed table never opens again,
   so a slower answer that still shows it open is old news, and the poll has
   stopped and will not correct it. */
const closedLobbies = new Set<string>();

export const useSessionStore = create<SessionState>((set, get) => {
  /** The search is over: nothing is waiting, and nothing is wrong. `ticket` is how it ended, if known. */
  const endSearch = (ticket: Ticket | null = null): void => {
    set({ ticket, queuedAt: null, ticketError: null });
  };

  /**
   * Every lobby mutation answers with the whole lobby; adopt it verbatim.
   * Resolves whether the server took the change, which the shared
   * `lobbyError` cannot say: the poll writes it too.
   */
  const mutate = async (
    call: (client: ApiClient, code: string) => Promise<Lobby>,
  ): Promise<boolean> => {
    const { client, lobby } = get();
    if (lobby === null) return false;
    return (await lobbyRequest(() => call(client, lobby.code))) !== null;
  };

  /**
   * Runs one lobby request under `busy` and adopts the lobby it answers with.
   * A reply or a failure that lands after a reset belongs to a table the
   * player has left: it changes nothing, and the request resolves null.
   */
  const lobbyRequest = async (request: () => Promise<Lobby>): Promise<Lobby | null> => {
    const current = lobbyRequests.observe();
    set({ busy: true, lobbyError: null });
    try {
      const lobby = await request();
      if (!current()) return null;
      adoptLobby(lobby);
      return lobby;
    } catch (error) {
      if (current()) set({ lobbyError: toFriendlyError(error) });
      return null;
    } finally {
      if (current()) set({ busy: false });
    }
  };

  const adoptLobby = (lobby: Lobby): void => {
    if (lobby.status === 'closed') closedLobbies.add(lobby.code);
    else if (closedLobbies.has(lobby.code)) return;
    set({ lobby, lobbyError: null });
    if (lobby.game_id !== null) {
      set({ gameId: lobby.game_id });
      get().stopWatchingLobby();
    }
  };

  return {
    client: api,
    schedule: timerSchedule,

    ticket: null,
    queuedAt: null,
    ticketError: null,

    lobby: null,
    lobbyError: null,
    busy: false,

    gameId: null,
    strayGameId: null,
    strayFor: null,

    async enterQueue(request) {
      // One queue at a time, but a ticket that has already matched or expired
      // is history and must not block the next search.
      if (entering || ticketPoll !== null) return;
      const existing = get().ticket;
      if (
        existing !== null &&
        existing.id !== cancelling &&
        existing.status === 'queued' &&
        existing.game_id === null
      ) {
        /*
         * Still searching, with nothing watching: the screen that started it
         * went away without canceling. The same search is resumed. A different
         * one is retired first; returning here instead left the player looking
         * at a search that nothing was running.
         */
        if (answersRequest(existing, request)) {
          get().watchTicket(existing.id);
          return;
        }
        /*
         * The generation is observed after `retire` supersedes the last one
         * and before the cancel settles: a Cancel pressed during that wait
         * begins another, and this search must stand down rather than post a
         * ticket nothing is watching.
         */
        const retiring = retire(existing);
        const standing = queueRequests.observe();
        await retiring;
        if (!standing() || entering || ticketPoll !== null) return;
      }
      entering = true;
      queueOffScreen = false;
      queueOwner = null;
      createsOut += 1;
      const current = queueRequests.begin();
      const { client } = get();
      set({ ticket: null, ticketError: null, gameId: null, queuedAt: Date.now() });
      try {
        await sequenceQueue(async () => {
          // Cancel can also overtake a replacement waiting on an older POST.
          if (!current()) return;
          let ticket = await client.queue({
            mode: request.mode,
            format: request.format,
            allowBotBackfill: request.allowBotBackfill,
          });
          if (!current()) {
            // Keep the next POST behind this DELETE: concurrent creates can
            // return the same ticket id, which would cancel the new search.
            await abandon(client, ticket);
            return;
          }
          if (!answersRequest(ticket, request)) {
            // The earlier ticket outlived its cancellation. Retire it and ask
            // again rather than searching in the format it still carries.
            await client.cancelTicket(ticket.id);
            if (!current()) return;
            ticket = await client.queue({
              mode: request.mode,
              format: request.format,
              allowBotBackfill: request.allowBotBackfill,
            });
            if (!current()) {
              await abandon(client, ticket);
              return;
            }
          }
          if (!answersRequest(ticket, request)) {
            // Still not ours. Say so rather than quietly queueing the player
            // into someone else's format.
            await client.cancelTicket(ticket.id).catch(() => undefined);
            throw new Error('the queue answered with a ticket for a different game');
          }
          set({ ticket });
          if (ticket.game_id !== null) set({ gameId: ticket.game_id });
          else get().watchTicket(ticket.id);
        });
      } catch (error) {
        if (current()) set({ ticketError: toFriendlyError(error), queuedAt: null });
        // A Cancel waiting on this create learns why it made nothing.
        else handOver(null, error);
      } finally {
        createsOut -= 1;
        if (current()) entering = false;
        // A superseded create that made nothing tells a waiting Cancel so.
        else handOver(null);
      }
    },

    watchTicket(id, immediate = false) {
      ticketPoll?.stop();
      const { client, schedule } = get();
      ticketPoll = poll({
        schedule,
        immediate,
        run: () => client.ticket(id),
        onResult: (ticket) => {
          // A Cancel that did not land stays said while the search runs on.
          const kept = get().ticketError;
          const running = ticket.status === 'queued' && ticket.game_id === null;
          set({
            ticket,
            ticketError: running && kept?.key === 'errors.cancelFailed' ? kept : null,
          });
          if (ticket.game_id !== null) {
            set({ gameId: ticket.game_id });
            ticketPoll = null;
            return 'stop';
          }
          if (ticket.status !== 'queued') {
            ticketPoll = null;
            return 'stop';
          }
          return 'continue';
        },
        onError: (error) => {
          set({ ticketError: toFriendlyError(error) });
          if (isTransient(error)) return 'continue';
          ticketPoll = null;
          return 'stop';
        },
      });
    },

    stopWatchingTicket() {
      ticketPoll?.stop();
      ticketPoll = null;
    },

    async leaveQueue() {
      const creating = entering || createsOut > 0 || lateWork.current() !== null;
      const current = queueRequests.begin();
      entering = false;
      get().stopWatchingTicket();
      const { client, ticket, gameId } = get();
      // Already at a table: the screen enters it, and there is nothing to cancel.
      if (gameId !== null) return 'matched';
      const live = ticket?.status === 'queued' ? ticket : null;
      if (live === null) {
        endSearch();
        if (!creating) return 'left';
      } else {
        set({ ticketError: null });
      }
      /*
       * The ticket is kept until the server lets it go. A cancel that fails
       * leaves a live ticket the matcher can still seat, so the player is told
       * and the search keeps being watched (docs/dev/matchmaking.md). With no
       * ticket yet but a create still out, this waits for the create and
       * cancels what it made: Rails matches inline, so it may be a table.
       */
      const answer: { value: LeaveOutcome | Ticket; target: Ticket | null } = {
        value: 'left',
        target: live,
      };
      if (live === null) {
        const late = lateOutcome(client);
        const first = await settledWithin(late, get().schedule);
        if (first === 'late') {
          /* The create has not answered. The player is told the search may
             still be running; when it does answer, a table it found is
             entered if nothing has moved on since. */
          void late.then(({ value, ticket: made, error: lost }) => {
            if (!current()) return;
            if (typeof value !== 'string' && value.game_id !== null) {
              // No queue screen left to enter it: the app does (`StrayTableDoor`).
              if (queueOffScreen) {
                endSearch();
                set({ strayGameId: value.game_id, strayFor: queueOwner });
              } else {
                set({ ticket: value, gameId: value.game_id, ticketError: null });
              }
            } else if (value === 'failed' && made !== null) {
              /* Still live on the server: watch it, and the alert stays said.
                 With the screen gone nothing shows it, so the watch stays
                 stopped, as the screen leaves it; the expiry ends the search. */
              set({ ticket: made });
              if (!queueOffScreen) get().watchTicket(made.id, true);
            } else if (made !== null) {
              /* The cancel landed after all. The search is over, so say so
                 rather than leave "still running" up with nothing behind it. */
              const ended =
                typeof value === 'string' ? { ...made, status: 'cancelled' as const } : value;
              endSearch(ended);
            } else {
              /* The create made no ticket. If its reply was lost, Rails may
                 still have queued the player, so they get the connection
                 retry, which finds that search again. Otherwise nothing is
                 running, and the screen says the search stopped. */
              set({ ticketError: lostReply(lost) ?? { key: 'errors.unexpected', details: null } });
            }
          });
          if (current()) set({ ticketError: { key: 'errors.cancelFailed', details: null } });
          return 'failed';
        }
        answer.value = first.value.value;
        answer.target = first.value.ticket;
        const lostSearch = answer.target === null ? lostReply(first.value.error) : null;
        if (lostSearch !== null) {
          // The create's reply was lost: the search may still be running.
          if (current()) set({ ticketError: lostSearch });
          return 'failed';
        }
      } else {
        cancelling = live.id;
        try {
          await sequenceQueue(async () => {
            answer.value = await cancelOrRead(client, live.id);
          });
        } finally {
          if (cancelling === live.id) cancelling = null;
        }
      }
      const { value: outcome, target } = answer;
      if (typeof outcome !== 'string') {
        if (outcome.game_id === null) {
          if (current()) endSearch();
          return 'left';
        }
        if (current()) set({ ticket: outcome, gameId: outcome.game_id });
        return 'matched';
      }
      if (!current() || target === null) return outcome;
      if (outcome === 'left') {
        endSearch();
      } else {
        set({ ticket: target, ticketError: { key: 'errors.cancelFailed', details: null } });
        get().watchTicket(target.id, true);
      }
      return outcome;
    },

    queueScreenGone(owner) {
      queueOffScreen = true;
      queueOwner = owner;
    },

    strayTableTaken() {
      set({ strayGameId: null, strayFor: null });
    },

    leaveForTable(gameId, owner) {
      if (get().gameId !== gameId || get().ticket?.game_id !== gameId) return;
      endSearch();
      set({ gameId: null, strayGameId: gameId, strayFor: owner });
    },

    recheckTicket() {
      const { ticket } = get();
      if (ticket?.status !== 'queued' || ticket.game_id !== null) return;
      set({ ticketError: null });
      get().watchTicket(ticket.id, true);
    },

    async createLobby(mode, format, ruleset) {
      set({ gameId: null });
      const { client } = get();
      const lobby = await lobbyRequest(() => client.createLobby(mode, format, ruleset));
      return lobby?.code ?? null;
    },

    async joinLobby(code) {
      set({ gameId: null });
      const { client } = get();
      const lobby = await lobbyRequest(async () => {
        await leaveSettled(code);
        return client.joinLobby(code);
      });
      return lobby?.code ?? null;
    },

    /*
     * By the route's code, not the store's table: a Leave tapped before the
     * first read lands has no table in the store, and one tapped on the way
     * to another table would find the last one there.
     */
    async leaveLobby(code) {
      const { client } = get();
      get().reset();
      const slot = leaving.get(code) ?? flight();
      leaving.set(code, slot);
      await slot.after(async () => {
        try {
          await client.leaveLobby(code);
        } catch {
          // Leaving is a local decision, as with the queue. A failed leave
          // keeps the seat, and the link gives it back. A host's failed leave
          // keeps the table open for the guests still at it.
        }
      });
    },

    watchLobby(code) {
      lobbyPoll?.stop();
      const { client, schedule } = get();
      lobbyPoll = poll({
        schedule,
        immediate: true,
        run: () => client.lobby(code),
        onResult: (lobby) => {
          adoptLobby(lobby);
          // A closed table never opens again, so there is nothing left to watch for.
          return lobby.game_id === null && lobby.status !== 'closed' ? 'continue' : 'stop';
        },
        onError: (error) => {
          set({ lobbyError: toFriendlyError(error) });
          if (isTransient(error)) return 'continue';
          lobbyPoll = null;
          return 'stop';
        },
      });
    },

    stopWatchingLobby() {
      lobbyPoll?.stop();
      lobbyPoll = null;
    },

    addBot: (seat, difficulty) => mutate((client, code) => client.addBot(code, seat, difficulty)),
    removeBot: (seat) => mutate((client, code) => client.removeBot(code, seat)),
    startLobby: () => mutate((client, code) => client.startLobby(code)),

    async talkInLobby(id) {
      const { client, lobby } = get();
      if (lobby === null) return false;
      const current = lobbyRequests.observe();
      try {
        const heard = await client.lobbyTalk(lobby.code, id);
        if (!current()) return false;
        adoptLobby(heard);
        return true;
      } catch {
        return false;
      }
    },

    reset() {
      queueRequests.retire();
      lobbyRequests.retire();
      closedLobbies.clear();
      get().stopWatchingTicket();
      get().stopWatchingLobby();
      entering = false;
      queueOffScreen = false;
      queueOwner = null;
      // `strayGameId` survives, with `strayFor`: it is a table the player is still owed a way into.
      set({
        ticket: null,
        queuedAt: null,
        ticketError: null,
        lobby: null,
        lobbyError: null,
        busy: false,
        gameId: null,
      });
    },
  };
});

/**
 * Whether this seat is the player's own. Two bot seats at one table share a
 * player id, so a bot seat is never a match for a person: only a human seat
 * can be theirs. Rails asks the same of a seat with `LobbyMember#held_by?`.
 */
export function isOwnSeat(member: LobbyMember, playerId: string | null): boolean {
  return !member.bot && playerId !== null && member.player_id === playerId;
}

/** The seat this device occupies, or null while spectating the lobby list. */
export function memberFor(lobby: Lobby | null, playerId: string | null): LobbyMember | null {
  if (lobby === null || playerId === null) return null;
  return lobby.members.find((member) => isOwnSeat(member, playerId)) ?? null;
}

export function isHost(lobby: Lobby | null, playerId: string | null): boolean {
  return lobby !== null && playerId !== null && lobby.host_id === playerId;
}

/** Seats a format needs filled before the host may start. */
export function seatsRequired(format: GameFormat): number {
  if (format === '2p') return 2;
  if (format === '3p') return 3;
  return 4;
}

/** Every seat is filled. Joining is the confirmation to play: no seat says it is ready. */
export function canStart(lobby: Lobby | null): boolean {
  return lobby !== null && lobby.members.length >= seatsRequired(lobby.format);
}
